import { useRouteContext } from '@tanstack/react-router'
import { LeaDataModel, EffectiveDateRecord, ENRL, CLEN, ATTN, LANG, SLNG, PRGM, MutationResponse, GlobalStateModel } from '@/models'
import { FAMI } from '@/models/fami'
import { buildRecordMutation, buildFileUploadMutation } from '@/utils/recordMutation'
import { ISODateStringToYYYYMMDD } from '@/utils/util'

export type CHLD = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    PreschoolName?: string // Only returned by the ChildDetail endpoint
    CSPPID: string
    SSID: string
    LocalChildId: string
    ChildLastName: string
    ChildFirstName: string
    ChildMiddleName: string
    ChildNameSuffix: string
    ChildDateOfBirth: string
    ChildGender: string
    ChildEthnicity: string
    ChildEthnicityMissingIndicator: string
    ChildRace1: string
    ChildRace2: string
    ChildRace3: string
    ChildRace4: string
    ChildRace5: string
    ChildRaceMissingIndicator: string
    ChildCityOfBirth: string
    ChildStateOfBirth: string
    ChildCountryOfBirth: string
    IEPIFSPIndicator: string

    FamilyRecords: Array<FAMI> | undefined
    LangRecords: Array<LANG> | undefined
    SLngRecords: Array<SLNG> | undefined
    Enrollments: Array<ENRL> | undefined
    ClassroomAssignments: Array<CLEN> | undefined
    Attendance: Array<ATTN> | undefined
    EducationPrograms: Array<PRGM> | undefined
} & EffectiveDateRecord

export const CHLD_TEMPLATE: CHLD = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    LocalChildId: '',
    CSPPID: '',
    SSID: '',
    ChildLastName: '',
    ChildFirstName: '',
    ChildMiddleName: '',
    ChildNameSuffix: '',
    ChildGender: '',
    ChildDateOfBirth: '',
    ChildCityOfBirth: '',
    ChildStateOfBirth: '',
    ChildCountryOfBirth: '',
    //DateOfEnrollmentStart: '',
    //DateOfEnrollmentEnd: '',
    //EligibilityStatus: '',
    //IEPorIFSP: '',
    IEPIFSPIndicator: '',
    ChildEthnicity: '',
    ChildEthnicityMissingIndicator: '',
    ChildRace1: '',
    ChildRace2: '',
    ChildRace3: '',
    ChildRace4: '',
    ChildRace5: '',
    ChildRaceMissingIndicator: '',
    EffectiveStartDate: '',
    EffectiveEndDate: '',
    FamilyRecords: [],
    LangRecords: [],
    SLngRecords: [],
    Enrollments: [],
    ClassroomAssignments: [],
    Attendance: [],
    EducationPrograms: [],
}

export function CHLD_Key(x: CHLD): string {
    return x.CSPPID
}

function toIsoDate(value: string | null | undefined): string {
    if (!value) return ''
    const trimmed = String(value).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
    const mdy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (mdy) {
        const [, m, d, y] = mdy
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
    }
    return trimmed
}

export function updateCHLD(c: CHLD, hasRole: (...roles: string[]) => boolean | undefined, lea_data: LeaDataModel) : CHLD {
    c.FamilyRecords = !hasRole('FAMIView') ? undefined : lea_data.fami.filter((e) => e.CSPPID === c.CSPPID)
    c.Enrollments = !hasRole('ENRLView') ? undefined : lea_data.enrl.filter((e) => e.CSPPID === c.CSPPID)
    c.ClassroomAssignments = !hasRole('CLENView') ? undefined : lea_data.clen.filter((e) => e.CSPPID === c.CSPPID)
    c.Attendance = !hasRole('ATTNView') ? undefined : lea_data.attn.filter((e) => e.CSPPID === c.CSPPID)
    c.LangRecords = !hasRole('LANGView') ? undefined : lea_data.lang.filter((e) => e.CSPPID === c.CSPPID)
    c.SLngRecords = !hasRole('SLNGView') ? undefined : lea_data.slng.filter((e) => e.CSPPID === c.CSPPID)
    c.EducationPrograms = !hasRole('PRGMView') ? undefined : (lea_data.prgm ?? []).filter((e) => e.CSPPID === c.CSPPID)
    return c
}

export function asSerializeable(chld: CHLD) {
    return {
        'ReportingLEA': chld.ReportingLEA,
        'PreschoolCode': chld.PreschoolCode,
        'ProgramYear': chld.ProgramYear,
        'LocalChildId': chld.LocalChildId,
        'CSPPID': chld.CSPPID,
        'SSID': chld.SSID,
        'ChildLastName': chld.ChildLastName,
        'ChildFirstName': chld.ChildFirstName,
        'ChildMiddleName': chld.ChildMiddleName,
        'ChildNameSuffix': chld.ChildNameSuffix,
        'ChildDateOfBirth': chld.ChildDateOfBirth,
        'ChildGender': chld.ChildGender,
        'ChildEthnicity': (chld.ChildEthnicity === '') ? '' : (chld.ChildEthnicity === 'Y'),
        'ChildEthnicityMissingIndicator': (chld.ChildEthnicityMissingIndicator === 'Y'),
        'ChildRace1': chld.ChildRace1,
        'ChildRace2': chld.ChildRace2,
        'ChildRace3': chld.ChildRace3,
        'ChildRace4': chld.ChildRace4,
        'ChildRace5': chld.ChildRace5,
        'ChildRaceMissingIndicator': (chld.ChildRaceMissingIndicator === 'Y'),
        'ChildCityOfBirth': chld.ChildCityOfBirth,
        'ChildStateOfBirth': chld.ChildStateOfBirth,
        'ChildCountryOfBirth': chld.ChildCountryOfBirth,
        'IEPIFSPIndicator': chld.IEPIFSPIndicator,
        'EffectiveStartDate': toIsoDate(chld.EffectiveStartDate),
    }
}

export function asSerializeableENRL(enrl: ENRL) {
    return {
        Id: enrl.Id,
        ReportingLEA: enrl.ReportingLEA,
        PreschoolCode: enrl.PreschoolCode,
        ProgramYear: enrl.ProgramYear,
        CSPPID: enrl.CSPPID,
        LocalChildId: enrl.LocalChildId,
        ContractType: enrl.ContractType,
        EnrollmentStartDate: enrl.EnrollmentStartDate,
        EnrollmentEndDate: enrl.EnrollmentEndDate,
        EnrollmentEndReason: enrl.EnrollmentEndReason,
        ProgramType: enrl.ProgramType,
        ProgramDuration: enrl.ProgramDuration,
        //TimeBase: enrl.TimeBase,
        EligibilityStatus: enrl.EligibilityStatus,
    }
}

export function asSerializeableCLEN(clen: CLEN) {
    return {
        Id: clen.Id,
        ReportingLEA: clen.ReportingLEA,
        PreschoolCode: clen.PreschoolCode,
        ProgramYear: clen.ProgramYear,
        CSPPID: clen.CSPPID,
        LocalChildId: clen.LocalChildId,
        LocalClassroomId: clen.LocalClassroomId,
        ClassroomEnrollmentStart: clen.ClassroomEnrollmentStart,
        ClassroomEnrollmentEnd: clen.ClassroomEnrollmentEnd,
    }
}

export function asSerializeableATTN(attn: ATTN) {
    return {
        Id: attn.Id,
        ReportingLEA: attn.ReportingLEA,
        PreschoolCode: attn.PreschoolCode,
        ProgramYear: attn.ProgramYear,
        LocalFamilyId: attn.LocalFamilyId,
        CSPPID: attn.CSPPID,
        LocalChildId: attn.LocalChildId,
        LocalClassroomId: attn.LocalClassroomId,
        FirstOfMonth: attn.FirstOfMonth,
        DaysExpected: attn.DaysExpected,
        DaysAttended: attn.DaysAttended,
        DaysAbsentExcused: Number(attn.DaysAbsentExcused),
        DaysAbsentUnexcused: Number(attn.DaysAbsentUnexcused),
        DaysAbsentSuspension: Number(attn.DaysAbsentSuspension),
        DaysAttendedSuspension: Number(attn.DaysAttendedSuspension),
    }
}

export function asSerializeableLANG(lang: LANG) {
    return {
        Id: lang.Id,
        ReportingLEA: lang.ReportingLEA,
        PreschoolCode: lang.PreschoolCode,
        ProgramYear: lang.ProgramYear,
        CSPPID: lang.CSPPID,
        LocalChildId: lang.LocalChildId,
        InstrumentOptOut: (lang.InstrumentOptOut === 'Y'),
        InstrumentDate: lang.InstrumentDate,
        InstrumentLanguagesHeard1: lang.InstrumentLanguagesHeard1,
        InstrumentLanguagesHeard2: lang.InstrumentLanguagesHeard2,
        InstrumentLanguagesHeard3: lang.InstrumentLanguagesHeard3,
        InstrumentLanguagesHeard4: lang.InstrumentLanguagesHeard4,
        InstrumentLanguagesHeard5: lang.InstrumentLanguagesHeard5,
        InstrumentLanguagesUnderstood1: lang.InstrumentLanguagesUnderstood1,
        InstrumentLanguagesUnderstood2: lang.InstrumentLanguagesUnderstood2,
        InstrumentLanguagesUnderstood3: lang.InstrumentLanguagesUnderstood3,
        InstrumentLanguagesUnderstood4: lang.InstrumentLanguagesUnderstood4,
        InstrumentLanguagesUnderstood5: lang.InstrumentLanguagesUnderstood5,
        InstrumentLanguagesSpoken1: lang.InstrumentLanguagesSpoken1,
        InstrumentLanguagesSpoken2: lang.InstrumentLanguagesSpoken2,
        InstrumentLanguagesSpoken3: lang.InstrumentLanguagesSpoken3,
        InstrumentLanguagesSpoken4: lang.InstrumentLanguagesSpoken4,
        InstrumentLanguagesSpoken5: lang.InstrumentLanguagesSpoken5,
        DLLInstrumentDesignated: (lang.DLLInstrumentDesignated === 'Y'),
        DLLTeacherDesignated: (lang.DLLTeacherDesignated === 'Y'),
        DLLTeacherDesignatedDate: lang.DLLTeacherDesignatedDate,
        EffectiveStartDate: lang.EffectiveStartDate,
    }
}

export function asSerializeableSLNG(slng: SLNG) {
    return {
        Id: slng.Id,
        ReportingLEA: slng.ReportingLEA,
        PreschoolCode: slng.PreschoolCode,
        ProgramYear: slng.ProgramYear,
        CSPPID: slng.CSPPID,
        LocalChildId: slng.LocalChildId,
        InterviewHomeLanguage1: slng.InterviewHomeLanguage1,
        InterviewHomeLanguage2: slng.InterviewHomeLanguage2,
        InterviewHomeLanguage3: slng.InterviewHomeLanguage3,
        InterviewHomeLanguage4: slng.InterviewHomeLanguage4,
        InterviewHomeLanguage5: slng.InterviewHomeLanguage5,
        InterviewMostUsedLanguage: slng.InterviewMostUsedLanguage,
        EffectiveStartDate: slng.EffectiveStartDate,
    }
}

export function mutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<CHLD>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildRecordMutation<CHLD>(leaContext as GlobalStateModel, setSubmitResult, asSerializeable, 'EditChildAjax')
}

export function enrlMutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<ENRL>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildRecordMutation<ENRL>(leaContext as GlobalStateModel, setSubmitResult, asSerializeableENRL, 'EditENRLAjax')
}

export function clenMutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<CLEN>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildRecordMutation<CLEN>(leaContext as GlobalStateModel, setSubmitResult, asSerializeableCLEN, 'EditCLENAjax')
}

export function attnMutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<ATTN>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildRecordMutation<ATTN>(leaContext as GlobalStateModel, setSubmitResult, asSerializeableATTN, 'EditATTNAjax')
}

export function langMutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<LANG>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildRecordMutation<LANG>(leaContext as GlobalStateModel, setSubmitResult, asSerializeableLANG, 'EditLANGAjax')
}

export function slngMutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<SLNG>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildRecordMutation<SLNG>(leaContext as GlobalStateModel, setSubmitResult, asSerializeableSLNG, 'EditSLNGAjax')
}

export function asSerializeablePRGM(prgm: PRGM): Record<string, string> {
    return {
        ReportingLEA: prgm.ReportingLEA ?? '',
        PreschoolCode: prgm.PreschoolCode ?? '',
        ProgramYear: prgm.ProgramYear ?? '',
        CSPPID: prgm.CSPPID ?? '',
        LocalChildId: prgm.LocalChildId ?? '',
        EduProgramCode: prgm.EduProgramCode ?? '',
        EduProgramMembershipStartDate: ISODateStringToYYYYMMDD(prgm.EduProgramMembershipStartDate),
        EduProgramMembershipEndDate: ISODateStringToYYYYMMDD(prgm.EduProgramMembershipEndDate),
        HomelessDwellingTypeCode: prgm.HomelessDwellingTypeCode ?? '',
        UnaccompaniedYouthIndicator: prgm.UnaccompaniedYouthIndicator ?? '',
    }
}


const PRGM_CSV_COLUMNS = [
    'RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
    'CSPPID', 'LocalChildId', 'EduProgramCode', 'EduProgramMembershipStartDate', 'EduProgramMembershipEndDate',
    'HomelessDwellingTypeCode', 'UnaccompaniedYouthIndicator',
]

export function prgmMutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<PRGM>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildFileUploadMutation<PRGM>(leaContext as GlobalStateModel, setSubmitResult, asSerializeablePRGM, 'PRGM', PRGM_CSV_COLUMNS, (prgm) => `Edit PRGM for ${prgm.CSPPID}`)
}